// src/pages/AdminToolsPage.tsx
//
// Governança de tools (admin) — reforma UX §6.12 (wireframe §13 da SPEC):
//   - GRID de cards; cada card tem SWITCH ligar/desligar (is_active), chips de
//     atributo CLICÁVEIS (core · admin-only · mcp · companion-only — toggle
//     direto via PATCH, como no HTML) e o link azul "ver agentes";
//   - métricas no topo: total · core · admin-only · MCP · companion-only;
//   - filtros: busca · categoria · atributo · status (ativada/desativada);
//   - "Nova tool" (POST /v1/admin/tools → create_tool/Mario → pending_deploy+PR);
//   - "ver agentes" abre modal com 2 listas (com × sem acesso) e confirmação
//     em cada adição/remoção (PATCH /v1/admin/agent-tools).

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import type { AdminAgentTool, AgentListItem } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Wrench,
  ShieldCheck,
  AlertCircle,
  Plus,
  Search,
  Eye,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import '@/styles/wf-agents.css'; // chrome: .dhead/.dfilters/.search/.dnewbtn + .agt-sw
import '@/styles/wf-skills.css'; // anatomia dos cards (.skl-grid/.sklc)
import '@/styles/wf-tools.css';
import { AttributeChip } from '@/components/admin-tools/AttributeChip';
import { NewToolModal } from '@/components/admin-tools/NewToolModal';
import { ToolAgentsModal } from '@/components/admin-tools/ToolAgentsModal';
import {
  categoryOptions,
  computeToolMetrics,
  filterTools,
  type AttributeFilter,
  type StatusFilter,
  type ToolAttributeKey,
  type ToolRow,
} from '@/components/admin-tools/tools-helpers';

function ToolsGridSkeleton() {
  return (
    <div className="skl-grid">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="p-4">
          <Skeleton className="h-4 w-1/2 mb-2" />
          <Skeleton className="h-3 w-full mb-3" />
          <Skeleton className="h-6 w-3/4" />
        </Card>
      ))}
    </div>
  );
}

export default function AdminToolsPage() {
  const { tenant } = useAuth();
  const isAdmin = !!tenant?.is_admin;
  const [tools, setTools] = useState<ToolRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros — wireframe §13: busca · categoria · atributo · status.
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [attributeFilter, setAttributeFilter] = useState<AttributeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [selected, setSelected] = useState<ToolRow | null>(null);
  const [newToolOpen, setNewToolOpen] = useState(false);
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [allAgentTools, setAllAgentTools] = useState<AdminAgentTool[]>([]);
  const [savingFlag, setSavingFlag] = useState<string | null>(null);
  const [savingAgent, setSavingAgent] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Contagem de agentes por tool (pra mostrar no card sem abrir a modal).
  const agentCountByTool = useMemo(() => {
    const m = new Map<string, number>();
    for (const at of allAgentTools) {
      if (at.enabled) m.set(at.tool_slug, (m.get(at.tool_slug) ?? 0) + 1);
    }
    return m;
  }, [allAgentTools]);

  // Agentes da tool selecionada — carregados POR tool (tool_slug + limit alto)
  // ao abrir a modal, não filtrados do allAgentTools (que vem capado).
  const [selectedAgentTools, setSelectedAgentTools] = useState<AdminAgentTool[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  function reloadSelectedAgentTools(tool: ToolRow | null) {
    if (!tool) {
      setSelectedAgentTools([]);
      return Promise.resolve();
    }
    setLoadingAgents(true);
    return api
      .listAdminAgentTools({ tool_slug: tool.slug, limit: 2000 })
      .then((ats) => setSelectedAgentTools(ats.filter((at) => at.enabled)))
      .catch(() => {})
      .finally(() => setLoadingAgents(false));
  }

  useEffect(() => {
    void reloadSelectedAgentTools(selected);
  }, [selected]);

  useEffect(() => {
    api
      .listAgents()
      .then((list) => {
        // Dedup por slug — o endpoint pode trazer o mesmo agente 2x (global+tenant).
        const seen = new Set<string>();
        setAgents(list.filter((a) => (seen.has(a.slug) ? false : (seen.add(a.slug), true))));
      })
      .catch(() => setAgents([]));
  }, []);

  function reloadAgentTools() {
    return api.listAdminAgentTools({}).then(setAllAgentTools).catch(() => {});
  }

  // include_inactive=true — o filtro de status (ativada/desativada) é no front;
  // sem isso, tool desligada sumia da lista no próximo load.
  function reloadTools() {
    return api
      .listAdminTools({ include_inactive: true })
      .then((t) => setTools(t as ToolRow[]))
      .catch((err: Error) => setError(err.message ?? 'Falha ao carregar tools'));
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.listAdminTools({ include_inactive: true }),
      api.listAdminAgentTools({}),
    ])
      .then(([t, ats]) => {
        setTools(t as ToolRow[]);
        setAllAgentTools(ats);
      })
      .catch((err: Error) => setError(err.message ?? 'Falha ao carregar tools'))
      .finally(() => setLoading(false));
  }, []);

  async function updateToolFlag(
    tool: ToolRow,
    flag: ToolAttributeKey | 'is_active',
    value: boolean,
  ) {
    setSavingFlag(`${tool.slug}:${flag}`);
    setActionError(null);
    try {
      await api.patchAdminToolFlags(tool.slug, { [flag]: value });
      const apply = (t: ToolRow): ToolRow => ({ ...t, [flag]: value });
      setTools((prev) => prev.map((t) => (t.slug === tool.slug ? apply(t) : t)));
      setSelected((prev) => (prev && prev.slug === tool.slug ? apply(prev) : prev));
      // Core/companion-only mudam a atribuição → recarrega agentes da tool + counts.
      if (flag === 'is_core' || flag === 'is_companion_only')
        await Promise.all([reloadAgentTools(), reloadSelectedAgentTools(apply(tool))]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Falha ao salvar');
    } finally {
      setSavingFlag(null);
    }
  }

  async function toggleAgent(agentRef: string, enabled: boolean) {
    if (!selected) return;
    setSavingAgent(agentRef);
    setActionError(null);
    try {
      await api.patchAdminAgentTool(agentRef, selected.slug, enabled);
      await Promise.all([reloadAgentTools(), reloadSelectedAgentTools(selected)]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Falha ao atualizar agente');
    } finally {
      setSavingAgent(null);
    }
  }

  const visibleTools = useMemo(
    () =>
      filterTools(tools, {
        search,
        category: categoryFilter,
        attribute: attributeFilter,
        status: statusFilter,
      }),
    [tools, search, categoryFilter, attributeFilter, statusFilter],
  );

  const metrics = useMemo(() => computeToolMetrics(tools), [tools]);
  const categories = useMemo(() => categoryOptions(tools), [tools]);

  // Guard admin (incidente Pamela 2026-07-19): a rota vive por URL direta
  // (decisão do dono), mas o CONTEÚDO admin não renderiza pra tenant não-admin
  // — mesmo padrão do AdminPromotionsPage. O backend agora também exige
  // requireAdmin nos GETs; este guard evita a tela quebrada/exposta.
  if (!isAdmin) {
    return (
      <div className="p-4 md:p-6 space-y-5">
        <PageHeader title="Tools" description="Registry de tools do Core." />
        <Card className="p-8 text-center">
          <p className="text-sm font-semibold text-raios-text mb-1">Acesso restrito</p>
          <p className="text-xs text-raios-muted">
            Esta área requer a role <span className="font-mono">raios_admin</span>.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5">
      <PageHeader
        title="Tools"
        description="Registry de tools do Core. O switch liga/desliga a tool; os chips togglam os atributos; “ver agentes” gerencia quem tem acesso."
      />

      {/* Métricas — total · core · admin-only · MCP · companion-only (TOOL-02,
          wireframe .statgrid/.stat com tons run/wait/appr/done) */}
      <div className="wf-statgrid" style={{ margin: '14px 0' }}>
        <div className="wf-kpi">
          <div className="wf-kpi-value">{metrics.total}</div>
          <div className="wf-kpi-label">Total</div>
        </div>
        <div className="wf-kpi wf-kpi--run">
          <div className="wf-kpi-value">{metrics.core}</div>
          <div className="wf-kpi-label">Core</div>
        </div>
        <div className="wf-kpi wf-kpi--wait">
          <div className="wf-kpi-value">{metrics.adminOnly}</div>
          <div className="wf-kpi-label">Admin-only</div>
        </div>
        <div className="wf-kpi wf-kpi--appr">
          <div className="wf-kpi-value">{metrics.mcp}</div>
          <div className="wf-kpi-label">MCP</div>
        </div>
        <div className="wf-kpi wf-kpi--done">
          <div className="wf-kpi-value">{metrics.companionOnly}</div>
          <div className="wf-kpi-label">Companion-only</div>
        </div>
      </div>

      {/* Filtros + Nova tool (wireframe §13 .dhead > .dfilters + .dnewbtn) */}
      <div className="dhead">
        <div className="dfilters">
          <div className="search" style={{ flex: 1, minWidth: 170 }}>
            <Search className="ic" aria-hidden />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar tool"
              aria-label="Buscar tool"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label="Filtro de categoria"
          >
            <option value="all">Categoria: todas</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={attributeFilter}
            onChange={(e) => setAttributeFilter(e.target.value as AttributeFilter)}
            aria-label="Filtro de atributo"
          >
            <option value="all">Atributo: todos</option>
            <option value="is_core">core</option>
            <option value="requires_admin">admin-only</option>
            <option value="mcp_exposed">mcp</option>
            <option value="is_companion_only">companion-only</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label="Filtro de status"
          >
            <option value="all">Status: todas</option>
            <option value="active">ativada</option>
            <option value="inactive">desativada</option>
          </select>
        </div>
        <button type="button" className="dnewbtn" onClick={() => setNewToolOpen(true)}>
          <Plus className="ic" aria-hidden /> Nova tool
        </button>
      </div>

      {actionError && (
        <div className="rounded-md border border-raios-error/40 bg-raios-error/10 p-2 text-xs text-raios-error">
          {actionError}
        </div>
      )}

      {loading ? (
        <ToolsGridSkeleton />
      ) : error ? (
        <EmptyState icon={AlertCircle} title="Erro" description={error} />
      ) : visibleTools.length === 0 ? (
        <EmptyState title="Nenhuma tool" description="Ajuste a busca ou os filtros." icon={Wrench} />
      ) : (
        <div className="skl-grid">
          {visibleTools.map((t) => {
            const count = agentCountByTool.get(t.slug) ?? 0;
            return (
              <div key={t.slug} className={cn('sklc', !t.is_active && 'opacity-60')} style={{ cursor: 'default' }}>
                {/* Switch liga/desliga a tool — topo direito (wireframe .agt-sw absoluto) */}
                <label
                  className="agt-sw"
                  style={{ position: 'absolute', top: 14, right: 14, margin: 0 }}
                  title={t.is_active ? 'Tool ativa — clique pra desligar' : 'Tool inativa — clique pra ligar'}
                  data-disabled={savingFlag === `${t.slug}:is_active` || undefined}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    role="switch"
                    checked={!!t.is_active}
                    disabled={savingFlag === `${t.slug}:is_active`}
                    onChange={(e) => void updateToolFlag(t, 'is_active', e.target.checked)}
                    aria-label={t.is_active ? 'Desativar tool' : 'Ativar tool'}
                  />
                  <span className="sw-track" />
                </label>

                {/* Identidade (wireframe .sklc-h: ícone + slug + "categoria · write") */}
                <div className="sklc-h">
                  <div className="sklc-ic">
                    <Wrench className="ic" aria-hidden />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="sklc-name">{t.slug}</div>
                    <div className="sklc-slug">
                      {t.category} · {t.is_mutating ? 'write' : 'read'}
                      {!t.is_mutating && (
                        <ShieldCheck
                          className="inline size-3 ml-1 align-[-2px]"
                          aria-label="read-only"
                        />
                      )}
                    </div>
                    {t.status === 'pending_deploy' && (
                      <div className="skl-badges">
                        <span className="wf-badge wf-badge-amber">pending_deploy</span>
                      </div>
                    )}
                  </div>
                </div>

                {t.description && <p className="sklc-desc">{t.description}</p>}

                {/* Chips de atributo clicáveis — toggle direto (TOOL-01,
                    wireframe .tool-chips .chip ok/k/off) */}
                <div className="tool-chips">
                  <AttributeChip
                    active={!!t.is_core}
                    saving={savingFlag === `${t.slug}:is_core`}
                    onToggle={() => updateToolFlag(t, 'is_core', !t.is_core)}
                    label="core"
                    tone="ok"
                  />
                  <AttributeChip
                    active={!!t.requires_admin}
                    saving={savingFlag === `${t.slug}:requires_admin`}
                    onToggle={() => updateToolFlag(t, 'requires_admin', !t.requires_admin)}
                    label="admin-only"
                    tone="k"
                  />
                  <AttributeChip
                    active={!!t.mcp_exposed}
                    saving={savingFlag === `${t.slug}:mcp_exposed`}
                    onToggle={() => updateToolFlag(t, 'mcp_exposed', !t.mcp_exposed)}
                    label="mcp"
                  />
                  <AttributeChip
                    active={!!t.is_companion_only}
                    saving={savingFlag === `${t.slug}:is_companion_only`}
                    onToggle={() => updateToolFlag(t, 'is_companion_only', !t.is_companion_only)}
                    label="companion-only"
                  />
                </div>

                {/* Rodapé (wireframe .toolc-foot): contagem + "ver agentes" azul */}
                <div className="toolc-foot">
                  <span>
                    Agentes com acesso{' '}
                    <b>{t.is_core ? (t.is_companion_only ? 'companions' : 'todos') : count}</b>
                  </span>
                  <button
                    type="button"
                    className="wf-btn wf-btn-primary tsbtn"
                    onClick={() => setSelected(t)}
                  >
                    <Eye aria-hidden /> ver agentes
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal — agentes com acesso (2 listas + confirmação) (TOOL-03) */}
      <ToolAgentsModal
        tool={selected}
        onClose={() => setSelected(null)}
        agents={agents}
        assigned={selectedAgentTools}
        loadingAgents={loadingAgents}
        savingAgent={savingAgent}
        onToggleAgent={toggleAgent}
      />

      {/* Modal — Nova tool (TOOL-04) */}
      <NewToolModal
        open={newToolOpen}
        onClose={() => setNewToolOpen(false)}
        onCreated={() => {
          void reloadTools();
          void reloadAgentTools();
        }}
      />
    </div>
  );
}
